import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { ApiError } from '../../api/client';
import { Badge, type BadgeTone } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useMediaQuery } from '../../lib/useMediaQuery';
import {
  cancelBooking, createCheckoutSession, fetchMyBookings, formatPrice, reconcilePayment,
  releaseHold, simulatePayment, type MyBooking, type ReconcileStatus,
} from '../booking/bookingApi';
import {
  clearCheckoutHandoff, clearPaymentConfirming, consumeCheckoutHandoff, markPaymentConfirming,
  peekCheckoutHandoff,
} from '../booking/checkoutHandoff';
import { OUTSTANDING_KEY } from './outstanding';
import { usePaymentReturn } from './usePaymentReturn';
import '../booking/booking.css';

const STATUS_TONE: Record<string, BadgeTone> = {
  Confirmed: 'success',
  PendingPayment: 'warning',
  ApprovedPendingPayment: 'warning',
  PendingApproval: 'info',
  Rejected: 'danger',
  Cancelled: 'neutral',
  Expired: 'neutral',
  PaidNeedsResolution: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  PendingPayment: 'Awaiting payment',
  ApprovedPendingPayment: 'Approved — pay now',
  PendingApproval: 'Awaiting approval',
  PaidNeedsResolution: 'Paid — being resolved',
};

/** The booking's club-local calendar date (yyyy-MM-dd) — what the date filters compare against. */
const localDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });

const fmtStamp = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Europe/London' });

const fmtStampFull = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
  });

/**
 * The money position, derived the same way everywhere: FOC trumps all, then refunds, then
 * settled payments, then live holds (unpaid with the balance due), then free/no-charge rows.
 */
function paymentInfo(b: MyBooking): { label: string; tone: BadgeTone } {
  if (b.isFoc) return { label: 'FOC', tone: 'info' };
  if (b.refundedPence > 0) return { label: `Refunded ${formatPrice(b.refundedPence)}`, tone: 'neutral' };
  if (b.paidPence > 0) return { label: `Paid ${formatPrice(b.paidPence)}`, tone: 'success' };
  if (b.status === 'PendingPayment' || b.status === 'ApprovedPendingPayment') {
    return { label: `Unpaid — ${formatPrice(b.pricePence)} due`, tone: 'warning' };
  }
  if (b.pricePence === 0) return { label: 'Free', tone: 'neutral' };
  if (b.status === 'Cancelled' || b.status === 'Rejected' || b.status === 'Expired') {
    return { label: '—', tone: 'neutral' };
  }
  // Confirmed with a price but no payment and no FOC flag — placed by the club, not billed here.
  return { label: 'Not billed', tone: 'neutral' };
}

type SortKey =
  | 'when' | 'venue' | 'pitch' | 'team' | 'type' | 'price' | 'payment' | 'status'
  | 'requested' | 'approved';
type SortState = { key: SortKey; dir: 1 | -1 };

const SORT_COMPARERS: Record<SortKey, (a: MyBooking, b: MyBooking) => number> = {
  when: (a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc),
  venue: (a, b) => a.venueName.localeCompare(b.venueName),
  pitch: (a, b) => a.pitchName.localeCompare(b.pitchName),
  team: (a, b) => a.teamName.localeCompare(b.teamName),
  type: (a, b) => `${a.type} ${a.matchType ?? ''}`.localeCompare(`${b.type} ${b.matchType ?? ''}`),
  price: (a, b) => a.pricePence - b.pricePence,
  payment: (a, b) => paymentInfo(a).label.localeCompare(paymentInfo(b).label),
  status: (a, b) =>
    (STATUS_LABEL[a.status] ?? a.status).localeCompare(STATUS_LABEL[b.status] ?? b.status),
  requested: (a, b) => Date.parse(a.createdUtc) - Date.parse(b.createdUtc),
  // Never-approved rows sort after every stamped one, whichever direction is chosen.
  approved: (a, b) =>
    (a.approvedUtc ? Date.parse(a.approvedUtc) : Number.MAX_SAFE_INTEGER)
    - (b.approvedUtc ? Date.parse(b.approvedUtc) : Number.MAX_SAFE_INTEGER),
};

function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  numeric,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={numeric ? 'bk-table-num' : undefined}
      aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
    >
      <button type="button" className={`th-sort ${active ? 'th-sort-active' : ''}`} onClick={() => onSort(sortKey)}>
        {label}
        <span className="th-sort-arrow" aria-hidden>
          {active ? (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronsUpDown size={12} />}
        </span>
      </button>
    </th>
  );
}

export function MyBookingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [teamFilter, setTeamFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // null = the date-aware default order (soonest-first upcoming, most-recent-first with past).
  const [sort, setSort] = useState<SortState | null>(null);

  const bookings = useQuery({ queryKey: ['bookings', 'mine'], queryFn: fetchMyBookings });

  // The list covers every booking on the user's teams (admins: all teams) — narrow by team,
  // date range, and whether finished bookings show at all.
  const teamNames = useMemo(
    () => [...new Set((bookings.data ?? []).map((b) => b.teamName))].sort((a, b) => a.localeCompare(b)),
    [bookings.data],
  );

  // Only statuses actually present — a coach with no rejections never sees a Rejected option.
  const statuses = useMemo(
    () =>
      [...new Set((bookings.data ?? []).map((b) => b.status))].sort((a, b) =>
        (STATUS_LABEL[a] ?? a).localeCompare(STATUS_LABEL[b] ?? b),
      ),
    [bookings.data],
  );

  const rows = useMemo(() => {
    const now = Date.now();
    return (bookings.data ?? [])
      .filter(
        (b) =>
          (!teamFilter || b.teamName === teamFilter) &&
          (!statusFilter || b.status === statusFilter) &&
          (showPast || Date.parse(b.endUtc) >= now) &&
          (!fromDate || localDate(b.startUtc) >= fromDate) &&
          (!toDate || localDate(b.startUtc) <= toDate),
      )
      // Header sort wins; otherwise upcoming reads soonest-first and history most-recent-first.
      .sort((a, b) => {
        if (sort) {
          return (
            sort.dir * SORT_COMPARERS[sort.key](a, b)
            || Date.parse(b.startUtc) - Date.parse(a.startUtc)
          );
        }
        return showPast
          ? Date.parse(b.startUtc) - Date.parse(a.startUtc)
          : Date.parse(a.startUtc) - Date.parse(b.startUtc);
      });
  }, [bookings.data, teamFilter, statusFilter, showPast, fromDate, toDate, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  // Which booking's payment is being confirmed with Stripe right now — Pay/Cancel stay disabled
  // so a slow webhook can't leave a just-paid booking looking payable (and clickable) again.
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  // Lets the confirmation poll below stop touching state (and stop refetching) once the coach
  // has navigated away — the async loop outlives this component by design.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Stripe success landing — reconcile with Stripe NOW instead of hoping the webhook has landed.
  // Ref-guarded rather than cleanup-cancelled: stripping the query param below re-runs this
  // effect, and an effect cleanup here would cancel the very confirmation it just started (the
  // old setTimeout refresh died exactly that way, which is why the row sat on "Unpaid").
  const handledPaid = useRef(false);
  useEffect(() => {
    if (params.get('paid')) {
      if (handledPaid.current) return;
      handledPaid.current = true;
      // The success URL deliberately carries no booking id we would have to trust — the handoff
      // this tab recorded on its way OUT to Stripe names the booking that was just paid for.
      const bookingId = peekCheckoutHandoff();
      clearCheckoutHandoff();
      setParams({}, { replace: true });
      if (bookingId === null) {
        // Legacy series checkout, or a return this tab never started: no booking to reconcile,
        // so the webhook + refetches carry it, as before.
        toast('Payment received — your booking will confirm momentarily', 'success');
        window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] }), 2500);
        window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] }), 8000);
        return;
      }

      // The redirect reloaded the whole app, so React state can't warn the OTHER payment
      // surfaces (after-login nudge, My payments) — the marker rides sessionStorage instead,
      // and those surfaces hide this booking's row until confirmation lands.
      markPaymentConfirming(bookingId);
      setConfirmingId(bookingId);
      toast('Payment received — confirming your booking…', 'info');
      void (async () => {
        let outcome: ReconcileStatus = 'pending';
        try {
          outcome = (await reconcilePayment(bookingId)).status;
        } catch {
          // Server/network hiccup — the webhook is still coming; fall through to the poll.
        }
        // Webhook fallback: delayed settlement or an unreachable provider — give it a few
        // refetches before conceding the row will update on its own. Stops early if the coach
        // has navigated away; the marker + queries still settle either way.
        for (let i = 0; outcome === 'pending' && i < 4 && mounted.current; i++) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          await queryClient.refetchQueries({ queryKey: ['bookings', 'mine'] });
          const row = queryClient
            .getQueryData<MyBooking[]>(['bookings', 'mine'])
            ?.find((b) => b.id === bookingId);
          if (row?.status === 'PaidNeedsResolution') outcome = 'needs-resolution';
          else if (!row || row.paidPence > 0 || row.status === 'Confirmed') outcome = 'confirmed';
        }
        clearPaymentConfirming();
        if (mounted.current) {
          setConfirmingId(null);
          if (outcome === 'needs-resolution') {
            // Money taken, slot possibly NOT held (paid after a cancel/clash) — never call it
            // confirmed; the row's "Paid — being resolved" badge says the rest.
            toast('Payment received — there is a problem with this booking and the club is resolving it', 'info');
          } else {
            toast(
              outcome === 'confirmed'
                ? 'Payment received — your booking is confirmed'
                : 'Payment received — your booking will confirm shortly',
              'success',
            );
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] });
        await queryClient.invalidateQueries({ queryKey: OUTSTANDING_KEY });
      })();
      return;
    }
    // Backing out of Stripe is an explicit "no", so the held slot is freed straight away rather
    // than sitting held until the hold lapses — for a block-booking week the server releases the
    // WHOLE series, since every week shares one hold. The id rides on the cancel URL, but that is
    // only a claim — it must match a handoff this tab actually made, or a link from anyone else
    // would release the coach's live hold on load. Unmatched returns (including the legacy series
    // checkout's "cancelled=series") fall through to the leave-it-alone path. The server still
    // re-checks with Stripe that the coach didn't in fact pay before cancelling.
    const cancelled = params.get('cancelled');
    if (cancelled) {
      const bookingId = consumeCheckoutHandoff(cancelled);
      setParams({}, { replace: true });
      if (bookingId === null) {
        toast('Checkout cancelled — your hold stays live until it expires', 'info');
        return;
      }

      void (async () => {
        try {
          const result = await releaseHold(bookingId);
          toast(
            (result.releasedCount ?? 1) > 1
              ? `Checkout cancelled — all ${result.releasedCount} held weeks have been released, book again when you are ready`
              : 'Checkout cancelled — the slot has been released, book again when you are ready',
            'info',
          );
        } catch (err) {
          if (err instanceof ApiError && err.code === 'already-paid') {
            toast('Payment received — your booking will confirm momentarily', 'success');
          } else {
            // Provider unreachable, or the sweep got there first — never claim the slot is
            // free when we could not prove it.
            toast('Checkout cancelled — your hold stays live until it expires', 'info');
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] });
      })();
      return;
    }

    // Reached this page by any route other than the two Stripe redirects, so whatever handoff is
    // in storage belongs to a checkout the coach walked away from without coming back through
    // one — browser Back, or closing the Stripe page. Spend it here rather than leaving a marker
    // a later crafted return could be made to match.
    clearCheckoutHandoff();
  }, [params, setParams, toast, queryClient]);

  // Held past the mutation settling on purpose. Setting location only STARTS the navigation to
  // Stripe — the mutation resolves immediately after, so a spinner tied to isPending would clear
  // while the browser is still leaving, and the row would sit there looking like the click did
  // nothing. Reset only when the coach comes back without paying (browser Back / the PWA sheet's
  // Done button), which restores this page without reloading it.
  const [redirectingId, setRedirectingId] = useState<number | null>(null);
  usePaymentReturn(redirectingId, () => setRedirectingId(null));

  const pay = useMutation({
    mutationFn: async (id: number) => {
      try {
        const session = await createCheckoutSession(id);
        setRedirectingId(id);
        window.location.href = session.url;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'payments-not-configured') {
          try {
            await simulatePayment(id);
          } catch {
            throw new ApiError(503, {
              title: 'Online payment is not set up yet — contact the club to complete this booking',
            });
          }
          return 'simulated';
        }
        throw err;
      }
      return 'redirected';
    },
    onSuccess: (mode) => {
      if (mode === 'simulated') {
        toast('Payment simulated (dev mode) — booking confirmed', 'success');
        void queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] });
      }
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Payment failed to start', 'error'),
  });

  // Cancelling is destructive and (on cards especially) one tap away — always confirm first.
  const [cancelAsk, setCancelAsk] = useState<MyBooking | null>(null);

  const cancel = useMutation({
    mutationFn: (id: number) => cancelBooking(id, null),
    onSuccess: (result) => {
      setCancelAsk(null);
      // Paid bookings never auto-refund: set the expectation here, once, at the moment of
      // cancelling — an admin decides, and only an approved refund produces a follow-up email.
      if (result.refundReview) {
        toast(
          'Booking cancelled. Because it was paid for, an admin will review whether it is eligible for a refund and will process one if so.',
          'info',
        );
      } else {
        toast('Booking cancelled', 'success');
      }
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not cancel', 'error'),
  });

  // Cards on phones: the eleven-column table needs sideways scrolling there, which buried the
  // Pay/Cancel actions off-screen. Same rows, same filters — only the presentation switches.
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (bookings.isLoading) return <Spinner center />;

  const filtersActive = teamFilter !== '' || statusFilter !== '' || fromDate !== '' || toDate !== '';

  return (
    <div className="my-bookings">
      <h1 className="visually-hidden">Bookings</h1>
      <div className="booking-filter-row">
        {teamNames.length > 1 ? (
          <label htmlFor="mb-team-filter">
            Team{' '}
            <select
              id="mb-team-filter"
              className="ui-select"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <option value="">All teams</option>
              {teamNames.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label htmlFor="mb-status-filter">
          Status{' '}
          <select
            id="mb-status-filter"
            className="ui-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s] ?? s}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="mb-from">
          From{' '}
          <input
            id="mb-from"
            type="date"
            className="ui-input bk-date-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label htmlFor="mb-to">
          To{' '}
          <input
            id="mb-to"
            type="date"
            className="ui-input bk-date-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label className="bk-past-toggle">
          <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />{' '}
          Show past
        </label>
        <span className="bk-filter-count" aria-live="polite">
          {rows.length === 1 ? '1 booking' : `${rows.length} bookings`}
        </span>
      </div>
      {rows.length === 0 ? (
        filtersActive || !showPast ? (
          <Card title="No bookings match" subtitle="Widen the filters, or tick Show past for history." />
        ) : (
          <Card title="No bookings yet" subtitle="Book a pitch and it will appear here." />
        )
      ) : isMobile ? (
        <div className="mb-cards">
          {rows.map((b) => (
            <BookingCard
              key={b.id}
              booking={b}
              onPay={pay.mutate}
              onCancel={setCancelAsk}
              busy={pay.isPending || cancel.isPending || confirmingId !== null}
              payingId={redirectingId ?? (pay.isPending ? pay.variables : null)}
            />
          ))}
        </div>
      ) : (
        <div className="bk-table-wrap">
          <table className="bk-table">
            <thead>
              <tr>
                <SortableTh label="When" sortKey="when" sort={sort} onSort={toggleSort} />
                <SortableTh label="Venue" sortKey="venue" sort={sort} onSort={toggleSort} />
                <SortableTh label="Pitch" sortKey="pitch" sort={sort} onSort={toggleSort} />
                <SortableTh label="Team" sortKey="team" sort={sort} onSort={toggleSort} />
                <SortableTh label="Type" sortKey="type" sort={sort} onSort={toggleSort} />
                <SortableTh label="Price" sortKey="price" sort={sort} onSort={toggleSort} numeric />
                <SortableTh label="Payment" sortKey="payment" sort={sort} onSort={toggleSort} />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortableTh label="Requested" sortKey="requested" sort={sort} onSort={toggleSort} />
                <SortableTh label="Approved" sortKey="approved" sort={sort} onSort={toggleSort} />
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <BookingRow
                  key={b.id}
                  booking={b}
                  onPay={pay.mutate}
                  onCancel={setCancelAsk}
                  busy={pay.isPending || cancel.isPending || confirmingId !== null}
                  payingId={redirectingId ?? (pay.isPending ? pay.variables : null)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cancelAsk ? (
        <ConfirmDialog
          title="Cancel this booking?"
          body={
            `${cancelAsk.pitchName} on ${fmtStampFull(cancelAsk.startUtc)} goes back on sale.` +
            (cancelAsk.paidPence > cancelAsk.refundedPence
              ? ' It has been paid for — an admin will review whether a refund is due.'
              : '')
          }
          confirmLabel="Cancel booking"
          danger
          busy={cancel.isPending}
          onConfirm={() => cancel.mutate(cancelAsk.id)}
          onCancel={() => setCancelAsk(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * One booking as a phone-sized card — the same facts as a table row, with Pay/Cancel visible
 * instead of buried past a sideways scroll, and the rejection reason inline (title-attribute
 * tooltips do not exist on touch).
 */
function BookingCard({
  booking, onPay, onCancel, busy, payingId,
}: {
  booking: MyBooking;
  onPay: (id: number) => void;
  onCancel: (booking: MyBooking) => void;
  busy: boolean;
  payingId: number | null | undefined;
}) {
  const isPast = Date.parse(booking.endUtc) < Date.now();
  const when = new Date(booking.startUtc).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London',
  });
  const payment = paymentInfo(booking);

  return (
    <section className={`mb-card ${isPast ? 'mb-card-past' : ''}`} aria-label={`${booking.teamName} ${when}`}>
      <header className="mb-card-head">
        <span className="mb-card-when">
          {when}
          <span className="bk-table-muted"> {fmtTime(booking.startUtc)}–{fmtTime(booking.endUtc)}</span>
        </span>
        <Badge tone={STATUS_TONE[booking.status] ?? 'neutral'}>
          {STATUS_LABEL[booking.status] ?? booking.status}
        </Badge>
      </header>
      <div className="mb-card-body">
        <span className="mb-card-where">{booking.venueName} · {booking.pitchName}</span>
        <span className="mb-card-team">
          {booking.teamName}
          <span className="bk-table-muted">
            {' '}· {booking.type}{booking.matchType ? ` (${booking.matchType})` : ''}
          </span>
        </span>
        <span className="mb-card-money">
          <Badge tone={payment.tone}>{payment.label}</Badge>
          {booking.pricePence > 0 && booking.paidPence === 0 && !booking.isFoc ? (
            <span className="bk-table-muted">{formatPrice(booking.pricePence)}</span>
          ) : null}
        </span>
        {booking.decisionReason ? (
          <span className="mb-card-reason">{booking.decisionReason}</span>
        ) : null}
      </div>
      {booking.canPay || booking.canCancel ? (
        <footer className="mb-card-actions">
          {booking.canPay ? (
            <Button
              size="sm"
              loading={payingId === booking.id}
              disabled={busy}
              onClick={() => onPay(booking.id)}
            >
              {payingId === booking.id ? 'Opening…' : 'Pay'}
            </Button>
          ) : null}
          {booking.canCancel ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(booking)}>
              Cancel
            </Button>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}

function BookingRow({
  booking, onPay, onCancel, busy, payingId,
}: {
  booking: MyBooking;
  onPay: (id: number) => void;
  /** Opens the confirm dialog — cancelling is never a single un-confirmed click. */
  onCancel: (booking: MyBooking) => void;
  busy: boolean;
  /** Which row is opening checkout — the others only grey out, this one says why. */
  payingId: number | null | undefined;
}) {
  const isPast = Date.parse(booking.endUtc) < Date.now();
  const when = new Date(booking.startUtc).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Europe/London',
  });
  const payment = paymentInfo(booking);

  return (
    <tr className={isPast ? 'bk-table-past' : undefined}>
      <td className="bk-table-nowrap">
        {when}
        <span className="bk-table-muted"> {fmtTime(booking.startUtc)}–{fmtTime(booking.endUtc)}</span>
      </td>
      <td>{booking.venueName}</td>
      <td>{booking.pitchName}</td>
      <td>{booking.teamName}</td>
      <td className="bk-table-nowrap">
        {booking.type}
        {booking.matchType ? ` (${booking.matchType})` : ''}
      </td>
      <td className="bk-table-num">{booking.pricePence > 0 ? formatPrice(booking.pricePence) : '—'}</td>
      <td className="bk-table-nowrap">
        <Badge tone={payment.tone}>{payment.label}</Badge>
      </td>
      <td className="bk-table-nowrap" title={booking.decisionReason ?? undefined}>
        <Badge tone={STATUS_TONE[booking.status] ?? 'neutral'}>
          {STATUS_LABEL[booking.status] ?? booking.status}
        </Badge>
      </td>
      <td
        className="bk-table-nowrap"
        title={`Requested ${fmtStampFull(booking.createdUtc)} by ${booking.bookedBy}`}
      >
        {fmtStamp(booking.createdUtc)}
      </td>
      <td
        className="bk-table-nowrap"
        title={
          booking.approvedUtc
            ? `Approved ${fmtStampFull(booking.approvedUtc)}${booking.approvedBy ? ` by ${booking.approvedBy}` : ''}`
            : undefined
        }
      >
        {booking.approvedUtc ? fmtStamp(booking.approvedUtc) : '—'}
      </td>
      <td className="bk-table-actions">
        {booking.canPay ? (
          <Button
            size="sm"
            loading={payingId === booking.id}
            disabled={busy}
            onClick={() => onPay(booking.id)}
          >
            {payingId === booking.id ? 'Opening…' : 'Pay'}
          </Button>
        ) : null}
        {booking.canCancel ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(booking)}>
            Cancel
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
